import { Box, Collapse, IconButton, Stack, Typography, styled, useTheme } from "@mui/material";
import { ErrorBoundaryBaseLogic, IErrorBoundaryProps, IState } from "@smartrr/shared/components/ErrorBoundary";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { Cross, FaceSad, TriangleDown } from "akar-icons";
import React, { useCallback, useState } from "react";

import { AquariusModalBasic } from "@customer-app/account/views/OperationAquarius/components/AquariusModal";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { getFormattedCustomerSupportEmail } from "../AquariusInner/components/Subscription/utils";

const LogsButton = styled(Stack)<{ open: boolean }>`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;

  svg {
    transform: ${props => (props.open ? "rotate(360deg)" : "rotate(270deg)")};
    transition: 0.3s ease-in-out;
  }
`;

const ErrorMessageContainer = styled(Box)`
  background: #ffdada;
  border: 1px solid #ffbaba;
  border-radius: 7px;
  max-height: 300px;
  overflow-y: scroll;
  padding: 10px;
  position: relative;
`;

const AnimationWrapper = styled("div")`
  padding: 20px;
  [class*="error-message-wrapper"] {
    animation: shake 5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    animation-iteration-count: 1;
  }

  @keyframes shake {
    0% {
      transform: rotate(0);
    }
    15% {
      transform: rotate(1.5deg);
    }
    30% {
      transform: rotate(-1.5deg);
    }
    45% {
      transform: rotate(1deg);
    }
    60% {
      transform: rotate(-1deg);
    }
    75% {
      transform: rotate(0deg);
    }
    85% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(0deg);
    }
  }
`;

export const ErrorBoundary = ({
  children,
  useModalState,
  ...props
}: {
  children: React.ReactNode;
} & IErrorBoundaryProps) => (
  <ErrorBoundaryBaseLogic
    renderComponent={errProps =>
      useModalState ? <ErrorMessageModal {...errProps} /> : <ErrorMessage {...errProps} />
    }
    {...props}
  >
    {children}
  </ErrorBoundaryBaseLogic>
);

export const ErrorMessage = ({ error, info, resetErrorBoundary }: IState): JSX.Element => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const state = useSmartrrCustomerSelector(state => state.auth);
  const customerShopifyId = state.customerRelationship?.shopifyId
    ? shopifyGidToNumber(state.customerRelationship?.shopifyId!)
    : -1;
  const organization = state.organization;

  return (
    <AnimationWrapper>
      <AquariusCard
        className={prefixClassName("error-message-wrapper")}
        sx={{
          margin: "0 auto",
          width: "320px",
          boxShadow:
            "0px 6px 12px -6px rgba(24, 39, 75, 0.12), 0px 8px 24px -4px rgba(24, 39, 75, 0.08) !important",
          position: "relative",
        }}
      >
        {!!resetErrorBoundary && (
          <IconButton
            aria-label="close"
            onClick={resetErrorBoundary}
            className={prefixClassName("icon-button")}
            sx={{
              position: "absolute",
              top: "20px",
              right: "20px",
            }}
          >
            <Cross strokeWidth={2} size={18} color={theme.modernIconsColor} />
          </IconButton>
        )}
        <Stack spacing="10px" direction="column">
          <Stack direction="row" spacing="10px" alignContent="center">
            <FaceSad
              className={prefixClassName("error-message-icon")}
              strokeWidth={1.5}
              size={27}
              color={theme.modernIconsColor}
              style={{ margin: "auto 0" }}
            />
            <Typography variant="h2">{typedLocalize(`errorMessages.largeTitle`)}</Typography>
          </Stack>
          <Typography variant="body1" sx={{ opacity: "80%" }}>
            {typedLocalize(`errorMessages.body`)}
          </Typography>
          <Stack direction="row" spacing="10px">
            <AquariusButton
              sx={{ height: "30px", width: "max-content", margin: "0" }}
              href={
                organization ? getFormattedCustomerSupportEmail(organization!, customerShopifyId) : "no-email"
              }
            >
              {typedLocalize(`general.contactSupportBtn`)}
            </AquariusButton>
            <LogsButton open={open} onClick={() => setOpen(!open)} spacing={"4px"}>
              <TriangleDown color={theme.modernBodyFontColor} strokeWidth={0.8} size={20} />
              <Typography variant="caption1">{typedLocalize(`errorMessages.viewLogs`)}</Typography>
            </LogsButton>
          </Stack>
          {error || info ? (
            <Collapse in={open}>
              <ErrorMessageContainer>
                <Typography
                  variant="caption1"
                  sx={{
                    fontSize: "10px",
                    lineHeight: "15px",
                    whiteSpace: "pre-wrap",
                    display: "block",
                    maxWidth: "256px",
                  }}
                >
                  {!!error && error.toString()}
                  <br />
                  {!!info && info.componentStack}
                </Typography>
              </ErrorMessageContainer>
            </Collapse>
          ) : null}
        </Stack>
      </AquariusCard>
    </AnimationWrapper>
  );
};

export const ErrorMessageModal = ({ ...props }: IState): JSX.Element => {
  const [open, setOpen] = useState(true);

  /**
   * This component will already have been unmounted by the time `onClose` is called but
   * leaving this here just in case `resetErrorBoundary` doesn't reset the parent as expected,
   * modal should still be able to close as expected.
   *
   * Also disabling backdropClick so user is forced to invoke `resetErrorBoundary`
   */
  const onClose = useCallback(
    (e, reason) => {
      if (reason && reason == "backdropClick") {
        return;
      }
      setOpen(!open);
    },
    [open]
  );

  return (
    <AquariusModalBasic open={open!} onClose={onClose} disableEscapeKeyDown>
      <ErrorMessage {...props} />
    </AquariusModalBasic>
  );
};
